import type {
  BulkJobItemDerivedStatus,
  BulkJobItemForStatus,
  BulkJobStatus,
} from './types'

/**
 * Derive display status from progress counts.
 * - processing: not all items are done
 * - completed_with_failures: all done but some failed
 * - completed: all done and all succeeded
 */
export function deriveJobStatus(
  job: Pick<
    { total_items: number; processed_items: number; failed_items: number },
    'total_items' | 'processed_items' | 'failed_items'
  >
): BulkJobStatus {
  if (job.processed_items < job.total_items) {
    return 'processing'
  }
  return job.failed_items > 0 ? 'completed_with_failures' : 'completed'
}

/**
 * Derive the status of a bulk job item from its normalized fields.
 */
export function getItemDerivedStatus(
  item: BulkJobItemForStatus
): BulkJobItemDerivedStatus {
  if (item.error_message) {
    return 'error'
  }
  if (!item.result_entity_id) {
    return 'queued'
  }
  if (!item.generation_status) {
    return 'queued'
  }
  if (item.generation_status === 'completed') {
    return 'completed'
  }
  if (item.generation_status === 'failed') {
    return 'failed'
  }
  return 'generating'
}

/**
 * Count items that are "done" and items that "failed".
 * Accepts a mapper to normalize domain-specific item shapes.
 *
 * An item is done if:
 * - It has an error_message (queue error or timeout)
 * - Its generation status is 'completed' or 'failed'
 *
 * An item is failed if:
 * - It has an error_message
 * - Its generation status is 'failed'
 */
export function countItemStats<T>(
  items: T[],
  toStatusItem: (item: T) => BulkJobItemForStatus
): { processed: number; failed: number } {
  let processed = 0
  let failed = 0
  for (const item of items) {
    const { error_message, result_entity_id, generation_status } =
      toStatusItem(item)
    if (error_message) {
      processed++
      failed++
    } else if (result_entity_id && generation_status) {
      if (generation_status === 'completed') {
        processed++
      } else if (generation_status === 'failed') {
        processed++
        failed++
      }
    }
  }
  return { processed, failed }
}
